import { useState, useEffect } from "react";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  CheckCircle,
  Calendar,
  Clock,
  MapPin,
  User,
  Home,
  Loader2,
  QrCode,
  Mail,
  Phone,
  Hash,
  CreditCard,
  Crown,
  Star,
  Sparkles,
  Ticket,
  Download,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DetailRow = ({ icon, label, value, valueClass = "" }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex items-center gap-2 text-neutral-500 shrink-0">
      {icon}
      <span className="text-sm">{label}</span>
    </div>
    <span className={`text-sm text-white text-right truncate max-w-[55%] ${valueClass}`}>{value || "—"}</span>
  </div>
);

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const bookingNumber = searchParams.get("booking");
  const reference = searchParams.get("reference");

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useScrollToTopWhenLoading(loading);

  useEffect(() => {
    if (bookingNumber) {
      fetchBooking();
    } else if (reference) {
      // Verify payment and get booking
      verifyAndFetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingNumber, reference]);

  const handleDownloadReceipt = async () => {
    if (!booking?.booking_number) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/bookings/${booking.booking_number}/receipt`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${booking.booking_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Receipt download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingNumber}`);
      setBooking(res.data);
    } catch (error) {
      console.error("Error fetching booking:", error);
      setError("Booking not found");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndFetch = async () => {
    try {
      // Verify payment
      await axios.get(`${API}/payments/verify/${reference}`);
      // Then we need the booking number - this is a simplified flow
      setError("Please use the booking number from your confirmation");
    } catch (error) {
      setError("Payment verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-empire flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-empire-gold animate-spin mx-auto mb-4" />
          <p className="text-neutral-400">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="container-empire flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">!</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-neutral-400 mb-6">{error || "Booking not found"}</p>
          <Link to="/">
            <Button className="btn-empire">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const bookingType = booking.booking_type || "class";
  const danceClass = booking.dance_class || {};
  const schedule = booking.schedule || {};
  const instructor = booking.instructor || danceClass.instructor || {};
  const studio = booking.studio || {};
  const scheduleTitle = schedule.title || danceClass.name;
  const event = booking.event || {};
  const ticketType = booking.ticket_type || {};
  const pkg = booking.package || {};
  const startTime = schedule.start_datetime
    ? parseISO(schedule.start_datetime)
    : event.start_datetime ? parseISO(event.start_datetime) : null;
  const endTime = schedule.end_datetime
    ? parseISO(schedule.end_datetime)
    : event.end_datetime ? parseISO(event.end_datetime) : null;

  // Venue for the QR copy
  const venueName = studio.name || event.venue_name || null;

  // ── Per-type ticket config ──────────────────────────────────────────
  const tierKey = ticketType.ticket_type || "regular";

  const ticketConfig = {
    class: {
      label: "Class Ticket",
      sublabel: scheduleTitle || "Dance Class",
      headerBg: "bg-neutral-950",
      borderColor: "border-empire-gold/20",
      accentColor: "text-empire-gold/60",
      Icon: null,
      tierBadge: null,
    },
    event: {
      vip: {
        label: "VIP Experience",
        sublabel: event.name || "Special Event",
        headerBg: "bg-gradient-to-b from-purple-950 to-neutral-950",
        borderColor: "border-purple-500/40",
        accentColor: "text-purple-300/80",
        Icon: Crown,
        tierBadge: { text: "VIP", bg: "bg-purple-500/20 border-purple-400/40 text-purple-300" },
      },
      early_bird: {
        label: "Early Bird Ticket",
        sublabel: event.name || "Special Event",
        headerBg: "bg-gradient-to-b from-sky-950 to-neutral-950",
        borderColor: "border-sky-500/30",
        accentColor: "text-sky-300/80",
        Icon: Sparkles,
        tierBadge: { text: "Early Bird", bg: "bg-sky-500/20 border-sky-400/40 text-sky-300" },
      },
      regular: {
        label: "Event Ticket",
        sublabel: event.name || "Special Event",
        headerBg: "bg-gradient-to-b from-teal-950 to-neutral-950",
        borderColor: "border-teal-500/30",
        accentColor: "text-teal-300/80",
        Icon: Ticket,
        tierBadge: { text: "Regular", bg: "bg-teal-500/20 border-teal-400/40 text-teal-300" },
      },
    },
    package: {
      label: "Elite Pass",
      sublabel: pkg.name || "Dance Package",
      headerBg: "bg-gradient-to-b from-amber-950 to-neutral-950",
      borderColor: "border-amber-400/40",
      accentColor: "text-amber-300/80",
      Icon: Crown,
      tierBadge: { text: "Premium", bg: "bg-amber-500/20 border-amber-400/40 text-amber-300" },
    },
  };

  const cfg = bookingType === "event"
    ? (ticketConfig.event[tierKey] || ticketConfig.event.regular)
    : bookingType === "package"
      ? ticketConfig.package
      : ticketConfig.class;

  const TicketIcon = cfg.Icon;

  return (
    <div className="container-empire max-w-2xl" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-12 h-12 text-green-500" />
        </motion.div>

        <span className="font-cookie text-3xl text-empire-gold block mb-2">
          You're all set!
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Booking Confirmed
        </h1>
        <p className="text-neutral-400">
          We've sent a confirmation to <span className="text-white hover:text-empire-gold cursor-pointer" onClick={() => { navigator.clipboard.writeText(booking.guest_email); }}>{booking.guest_email}</span>
        </p>
      </motion.div>

      {/* Booking Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="card-empire-featured overflow-hidden">
          <CardContent className="p-0">
            {/* Header with booking number */}
            <div className="bg-empire-gold/10 border-b border-empire-gold/20 p-6 text-center">
              <p className="text-sm text-empire-gold mb-1">Booking Reference</p>
              <p className="text-2xl font-bold text-white font-mono tracking-wider" data-testid="booking-number">
                {booking.booking_number}
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* QR Code — boarding-pass / ticket style */}
              <div className="flex flex-col items-center gap-3" data-testid="qr-code">
                <div className="relative w-fit">

                  {/* ① Branded header — colour driven by ticket type */}
                  <div className={`${cfg.headerBg} rounded-t-2xl px-10 py-3 flex flex-col items-center gap-1.5 border ${cfg.borderColor} border-b-0`}>
                    <div className="flex items-center gap-2">
                      <img src="/branding/logo-light.png" alt="Empire Dance Kenya" className="h-8 w-auto object-contain" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] ${cfg.accentColor} tracking-[0.25em] uppercase font-semibold select-none`}>
                        {cfg.label}
                      </span>
                      {bookingType === "package" && (
                        <Crown className="w-3 h-3 fill-amber-400 text-amber-400" />
                      )}
                    </div>
                    {/* Tier badge for events */}
                    {cfg.tierBadge && (
                      <span className={`text-[9px] font-bold tracking-widest uppercase border rounded-full px-2.5 py-0.5 ${cfg.tierBadge.bg}`}>
                        {cfg.tierBadge.text}
                      </span>
                    )}
                  </div>

                  {/* ② Perforated divider */}
                  <div className={`relative h-6 bg-white border-x ${cfg.borderColor} flex items-center`}>
                    <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-neutral-900 z-10" />
                    <div className="flex-1 border-t-2 border-dashed border-neutral-200 mx-7" />
                    <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-neutral-900 z-10" />
                  </div>

                  {/* ③ White QR section */}
                  <div className={`bg-white rounded-b-2xl px-4 pb-4 pt-2 border ${cfg.borderColor} border-t-0`}>
                    <QRCodeSVG
                      value={booking.qr_code_hash || booking.booking_number}
                      size={188}
                      level="H"
                      includeMargin={false}
                    />
                  </div>

                </div>

                <p className="text-center text-sm text-neutral-400">
                  <QrCode className="w-4 h-4 inline mr-1" />
                  Show this QR code at {venueName ? <span className="text-white">{venueName}</span> : "the venue"} for check-in
                </p>
              </div>

              {/* Customer Details */}
              <div className="space-y-3 pt-2 border-t border-neutral-800">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Customer</p>
                <DetailRow icon={<User className="w-4 h-4 text-empire-gold" />} label="Name" value={booking.guest_name} />
                <DetailRow icon={<Mail className="w-4 h-4 text-empire-gold" />} label="Email" value={booking.guest_email} />
                <DetailRow icon={<Phone className="w-4 h-4 text-empire-gold" />} label="Phone" value={booking.guest_phone} />
                {studio.name && bookingType === "class" && (
                  <DetailRow icon={<MapPin className="w-4 h-4 text-empire-gold" />} label="Studio" value={studio.name} />
                )}
                {event.venue_name && bookingType === "event" && (
                  <DetailRow icon={<MapPin className="w-4 h-4 text-empire-gold" />} label="Venue" value={`${event.venue_name}${event.venue_address ? `, ${event.venue_address}` : ""}`} />
                )}
              </div>

              {/* Booking Details — adapts per type */}
              <div className="space-y-3 pt-2 border-t border-neutral-800">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  {bookingType === "class" ? "Class" : bookingType === "event" ? "Event" : "Package"}
                </p>

                {/* CLASS */}
                {bookingType === "class" && (<>
                  {scheduleTitle && <DetailRow icon={<Ticket className="w-4 h-4 text-empire-gold" />} label="Class" value={scheduleTitle} />}
                  {startTime && <DetailRow icon={<Calendar className="w-4 h-4 text-empire-gold" />} label="Date" value={format(startTime, "EEEE, MMMM d, yyyy")} />}
                  {startTime && <DetailRow icon={<Clock className="w-4 h-4 text-empire-gold" />} label="Time" value={endTime ? `${format(startTime, "h:mm a")} – ${format(endTime, "h:mm a")}` : format(startTime, "h:mm a")} />}
                  {instructor.name && <DetailRow icon={<User className="w-4 h-4 text-empire-gold" />} label="Instructor" value={instructor.name} />}
                </>)}

                {/* EVENT */}
                {bookingType === "event" && (<>
                  {event.name && <DetailRow icon={<Ticket className="w-4 h-4 text-empire-gold" />} label="Event" value={event.name} />}
                  {ticketType.name && (
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-neutral-500 shrink-0">
                        <Star className="w-4 h-4 text-empire-gold" />
                        <span className="text-sm">Ticket Type</span>
                      </div>
                      <span className={`text-xs font-bold tracking-wider uppercase border rounded-full px-3 py-1 ${cfg.tierBadge?.bg || "text-white border-neutral-600"}`}>
                        {ticketType.name}
                      </span>
                    </div>
                  )}
                  {booking.ticket_quantity > 1 && <DetailRow icon={<Hash className="w-4 h-4 text-empire-gold" />} label="Qty" value={`${booking.ticket_quantity} tickets`} />}
                  {startTime && <DetailRow icon={<Calendar className="w-4 h-4 text-empire-gold" />} label="Date" value={format(startTime, "EEEE, MMMM d, yyyy")} />}
                  {startTime && <DetailRow icon={<Clock className="w-4 h-4 text-empire-gold" />} label="Time" value={endTime ? `${format(startTime, "h:mm a")} – ${format(endTime, "h:mm a")}` : format(startTime, "h:mm a")} />}
                </>)}

                {/* PACKAGE */}
                {bookingType === "package" && (<>
                  {pkg.name && <DetailRow icon={<Crown className="w-4 h-4 text-amber-400" />} label="Package" value={pkg.name} valueClass="text-amber-300 font-semibold" />}
                  {pkg.validity_days && <DetailRow icon={<Calendar className="w-4 h-4 text-amber-400" />} label="Valid For" value={`${pkg.validity_days} days`} />}
                  {pkg.validity_days && booking.created_at && (() => {
                    const expiry = new Date(parseISO(booking.created_at).getTime() + pkg.validity_days * 86400000);
                    return <DetailRow icon={<Clock className="w-4 h-4 text-amber-400" />} label="Expires On" value={format(expiry, "MMMM d, yyyy")} valueClass="text-amber-300 font-semibold" />;
                  })()}
                </>)}
              </div>

              {/* Payment Details */}
              <div className="space-y-3 pt-2 border-t border-neutral-800">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Payment</p>
                <DetailRow icon={<CreditCard className="w-4 h-4 text-empire-gold" />} label="Amount Paid" value={`KES ${booking.total_amount?.toLocaleString()}`} valueClass="text-empire-gold font-bold text-base" />
                {booking.mpesa_receipt_number && (
                  <DetailRow icon={<Hash className="w-4 h-4 text-empire-gold" />} label="M-Pesa Ref" value={booking.mpesa_receipt_number} valueClass="font-mono text-xs" />
                )}
                {booking.payment_reference && (
                  <DetailRow icon={<Hash className="w-4 h-4 text-empire-gold" />} label="Transaction ID" value={booking.payment_reference} valueClass="font-mono text-xs" />
                )}
                <DetailRow icon={<Hash className="w-4 h-4 text-empire-gold" />} label="Booking Ref" value={booking.booking_number} valueClass="font-mono text-xs" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-4 mt-8 justify-center"
      >
        <Link to="/">
          <Button variant="outline" className="btn-empire-outline w-full sm:w-auto">
            <Home className="w-4 h-4 mr-2" />
            Book Another Class
          </Button>
        </Link>
        <Button
          className="btn-empire w-full sm:w-auto"
          onClick={handleDownloadReceipt}
          disabled={downloading}
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {downloading ? "Generating..." : "Download Receipt"}
        </Button>
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="mt-8 glass rounded-xl p-4 text-center"
      >
        <p className="text-sm text-neutral-400">
          Please arrive 10 minutes before your class starts. 
          If you need to cancel or reschedule, contact us at least 24 hours in advance.
        </p>
      </motion.div>
    </div>
  );
};
